<template>
  <div class="section">
    <div class="">
      <!-- Header -->
      <div class="level mb-5">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title is-3">Orders Dashboard</h1>
              
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <button class="button is-light is-small" @click="clearFilters">
              <span>Reset Filters</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="orders_loading" class="box has-text-centered">
        <progress class="progress is-large is-info" max="100">Loading...</progress>
      </div>

      <!-- Filters -->
      <div v-else class="box mb-5">
        <p class="heading has-text-grey mb-3">Filters</p>
        <div class="columns is-multiline">
          <div class="column is-4">
            <label class="label is-small">Range</label>
            <div class="tags">
              <span
                v-for="range in uniqueRanges"
                :key="range"
                class="tag is-medium is-clickable"
                :class="filterRanges.includes(range) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterRanges', range)"
              >
                {{ range }}
              </span>
            </div>
          </div>

          <div class="column is-4">
            <label class="label is-small">Order Type</label>
            <div class="tags">
              <span
                v-for="order_type in uniqueorder_types"
                :key="order_type"
                class="tag is-medium is-clickable"
                :class="filterorder_types.includes(order_type) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterorder_types', order_type)"
              >
                {{ order_type }}
              </span>
            </div>
          </div>

          <div class="column is-4">
            <label class="label is-small">Makeup Location</label>
            <div class="tags">
              <span
                v-for="location in uniqueLocations"
                :key="location"
                class="tag is-medium is-clickable"
                :class="filterLocations.includes(location) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterLocations', location)"
              >
                {{ location }}
              </span>
            </div>
          </div>
        </div>
        <p class="help has-text-grey">
          Showing <strong>{{ filteredOrders.length }}</strong> of <strong>{{ orders.length }}</strong> orders
          <span v-if="activeFilterCount > 0"> — {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} active</span>
        </p>
      </div>

      <!-- Summary Cards -->
      <div v-if="!orders_loading" class="columns is-multiline mb-5">
        <div class="column is-3">
          <div class="box has-background-info-light">
            <p class="heading">Total Orders</p>
            <p class="title">{{ totalOrders }}</p>
          </div>
        </div>
        <div class="column is-3">
          <div class="box has-background-success-light">
            <p class="heading">Total Qty</p>
            <p class="title">{{ totalQty }}</p>
          </div>
        </div>
        <div class="column is-3">
          <div class="box has-background-warning-light">
            <p class="heading">Total Printed</p>
            <p class="title">{{ totalQtyPrinted }}</p>
          </div>
        </div>
        <div class="column is-3">
          <div class="box has-background-danger-light">
            <p class="heading">Avg Qty/Order</p>
            <p class="title">{{ averageQty }}</p>
          </div>
        </div>
      </div>

      <!-- Orders Table -->
      <div v-if="!orders_loading" class="box mt-5">
        <h2 class="subtitle">Orders List</h2>
        <div class="table-container">
          <table class="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr class="is-selected">
                <th>PO Number</th>
                <th>Client Name</th>
                <th>Range</th>
                <th>Order Type</th>
                <th>Order Date</th>
                <th>Makeup Location</th>
                <th>Total Qty</th>
                <th>Qty Printed</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in filteredOrders" :key="order.ponumber">
                <td>
                  <span class="tag is-light">{{ order.ponumber }}</span>
                </td>
                <td>{{ order.clientname }}</td>
                <td>{{ order.range }}</td>
                <td>{{ order.order_type }}</td>
                <td>{{ formatDate(order.orderdate) }}</td>
                <td>{{ order.makeuplocation }}</td>
                <td>
                  <span class="tag is-info">{{ order.total_qty }}</span>
                </td>
                <td>
                  <span
                    :class="{
                      'tag is-success': order.qtyprinted === order.total_qty,
                      'tag is-warning': order.qtyprinted < order.total_qty,
                    }"
                  >
                    {{ order.qtyprinted }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="filteredOrders.length === 0" class="has-text-centered mt-5">
          <p class="subtitle is-5">No orders found matching your filters.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrderService from '@/services/OrderService.js';

export default {
  name: 'Overview',
  data() {
    return {
      orders: [
        {
          ponumber: 'PO-001',
          clientname: 'Acme Corp',
          range: 'Jerseys',
          order_type: 'Standard',
          orderdate: '2026-03-15',
          makeuplocation: 'Warehouse A',
          total_qty: 500,
          qtyprinted: 500,
        },
        {
          ponumber: 'PO-002',
          clientname: 'Tech Solutions',
          range: 'Leisurewear',
          order_type: 'Rush',
          orderdate: '2026-03-18',
          makeuplocation: 'Warehouse B',
          total_qty: 1000,
          qtyprinted: 850,
        },
        {
          ponumber: 'PO-003',
          clientname: 'Global Traders',
          range: 'Socks',
          order_type: 'Standard',
          orderdate: '2026-03-20',
          makeuplocation: 'Warehouse A',
          total_qty: 750,
          qtyprinted: 750,
        },
        {
          ponumber: 'PO-004',
          clientname: 'Acme Corp',
          range: 'Shorts',
          order_type: 'Express',
          orderdate: '2026-03-22',
          makeuplocation: 'Warehouse C',
          total_qty: 250,
          qtyprinted: 200,
        },
      ],
      orders_loading: false,
      accessToken: null,
      client_id: null,
      range: null,
      start_date: null,
      end_date: null,
      filterRanges: [],
      filterorder_types: [],
      filterLocations: [],
    };
  },
  created() {
    this.getAccessToken().then(() => {
      this.getOrders();
    });
  },
  methods: {
    async getAccessToken() {
      this.accessToken = await this.$auth.getTokenSilently();
    },
    async getOrders() {
      this.orders_loading = true;
      OrderService.getAggregatedOpenOrders(
        this.accessToken,
       
      ).then((orders) => {
        this.$set(this, 'orders', orders);
        this.orders_loading = false;
      });
    },
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
    clearFilters() {
      this.filterRanges = [];
      this.filterorder_types = [];
      this.filterLocations = [];
    },
    toggleFilter(filterKey, value) {
      const idx = this[filterKey].indexOf(value);
      if (idx === -1) {
        this[filterKey].push(value);
      } else {
        this[filterKey].splice(idx, 1);
      }
    },
  },
  computed: {
    uniqueRanges() {
      return [...new Set(this.orders.map((order) => order.range))].sort();
    },
    uniqueorder_types() {
      return [...new Set(this.orders.map((order) => order.order_type))].sort();
    },
    uniqueLocations() {
      return [...new Set(this.orders.map((order) => order.makeuplocation))].sort();
    },
    activeFilterCount() {
      return this.filterRanges.length + this.filterorder_types.length + this.filterLocations.length;
    },
    totalOrders() {
      return this.filteredOrders.length;
    },
    totalQty() {
      return this.filteredOrders.reduce((sum, order) => sum + order.total_qty, 0);
    },
    totalQtyPrinted() {
      return this.filteredOrders.reduce((sum, order) => sum + order.qtyprinted, 0);
    },
    averageQty() {
      return this.filteredOrders.length > 0
        ? (this.totalQty / this.filteredOrders.length).toFixed(2)
        : 0;
    },
    filteredOrders() {
      return this.orders.filter((order) => {
        const rangeMatch =
          this.filterRanges.length === 0 ||
          this.filterRanges.includes(order.range);
        const order_typeMatch =
          this.filterorder_types.length === 0 ||
          this.filterorder_types.includes(order.order_type);
        const locationMatch =
          this.filterLocations.length === 0 ||
          this.filterLocations.includes(order.makeuplocation);

        return rangeMatch && order_typeMatch && locationMatch;
      });
    },
  },
  mounted() {
    // Component mounted
  },
};
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

.section {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.tag.is-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag.is-clickable:hover {
  transform: scale(1.05);
}
</style>